<template>
  <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
    <div class="bg-white rounded-xl shadow-2xl w-[98vw] h-[95vh] flex flex-col overflow-hidden">
      <!-- Header -->
      <div class="bg-gray-50 border-b p-4 flex items-center justify-between">
        <div>
          <h2 class="text-xl font-semibold text-gray-800">Fixture Player Lines Timeline v2</h2>
          <div class="text-sm text-gray-500 mt-1">Fixture ID: {{ fixtureId }}</div>
        </div>
        <button 
          @click="$emit('close')"
          class="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Main Content Area -->
      <div class="flex-1 overflow-auto p-4 bg-gray-200">
        <!-- Horizontal scrollable container for the table -->
        <div class="overflow-x-auto">
          <!-- Loading State -->
          <div v-if="loading" class="flex items-center justify-center h-64">
            <div class="text-center">
              <i class="fas fa-spinner fa-spin text-2xl text-blue-500 mb-2"></i>
              <p class="text-gray-600">Loading data...</p>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="flex items-center justify-center h-64">
            <div class="text-center">
              <i class="fas fa-exclamation-triangle text-2xl text-red-500 mb-2"></i>
              <p class="text-red-600 mb-2">{{ error }}</p>
              <button @click="loadTimelineData" class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">
                Try Again
              </button>
            </div>
          </div>

          <!-- Content -->
          <div v-else>
            <!-- Legend for Target Time Filter -->
            <div v-if="showTargetTimeData && filters.targetTime" class="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
              <div class="flex items-center gap-4 text-sm">
                <div class="flex items-center gap-2">
                  <div class="w-4 h-4 rounded border-2 border-orange-400 bg-orange-50"></div>
                  <span class="text-orange-800 font-medium">Highlighted values were active at your target time</span>
                </div>
                <div class="flex items-center gap-2">
                  <div class="w-2 h-2 rounded-full bg-orange-500"></div>
                  <span class="text-orange-700">Orange dot indicates target time values</span>
                </div>
              </div>
            </div>

            <!-- Grid Header -->
            <div class="bg-gradient-to-r from-slate-800 to-slate-900 rounded-t-xl min-w-[2400px] shadow-lg">
              <div class="flex items-center p-4 text-white text-sm font-semibold">
                <div class="w-40 flex-shrink-0">Player</div>
                <div class="flex-1 grid grid-cols-12 gap-1 min-w-[2400px]">
                  <div class="text-center">Points</div>
                  <div class="text-center">Rebounds</div>
                  <div class="text-center">Assists</div>
                  <div class="text-center">3 Points</div>
                  <div class="text-center">Turnovers</div>
                  <div class="text-center">Blocks</div>
                  <div class="text-center">Steals</div>
                  <div class="text-center">PRA</div>
                  <div class="text-center">PR</div>
                  <div class="text-center">PA</div>
                  <div class="text-center">RA</div>
                  <div class="text-center">BS</div>
                </div>
              </div>
            </div>

            <!-- Grid Body -->
            <div class="bg-white border border-t-0 border-slate-200 rounded-b-xl min-w-[2400px] shadow-lg">
              <div v-if="timelineData.length === 0" class="text-center py-12">
                <i class="fas fa-chart-line text-4xl text-gray-300 mb-4"></i>
                <p class="text-gray-500 text-lg">No timeline data found</p>
                <p class="text-gray-400 text-sm">Try adjusting your filters or check if data exists for this fixture</p>
              </div>
              
              <div v-else>
                <!-- Group data by player -->
                <div v-for="player in enhancedGroupedData" :key="player.player_id" 
                     class="border-b border-slate-100 hover:bg-slate-50 transition-all duration-200 group">
                  <div class="flex items-start p-4 bg-gray-50">
                    <!-- Player Info -->
                    <div class="w-40 flex-shrink-0 flex items-center gap-3">
                      <div class="flex-1">
                        <div class="font-semibold text-slate-800 text-sm">{{ player.player_name }}</div>
                        <div class="text-xs text-slate-500 font-medium">{{ player.player_team_name }}</div>
                        <div class="text-xs text-slate-400">{{ formatTime(player.latest_entry?.created_at) }}</div>
                      </div>
                    </div>
                    
                    <!-- Market Grid with Timeline -->
                    <div class="flex-1 grid grid-cols-12 gap-1 min-w-[2400px]">
                      <div v-for="market in ['points', 'total_rebounds', 'assists', 'three_point_field_goal', 'turnovers', 'blocks', 'steals', 'pra', 'pr', 'pa', 'ra', 'bs']" 
                           :key="market" class="relative market-cell">
                        <div class="border border-slate-200 rounded-xl p-2 min-h-[140px] flex flex-col transition-all duration-300 hover:shadow-md hover:border-slate-300 bg-gray-100">
                          <!-- Market Header -->
                          <div class="text-xs font-semibold text-slate-600 text-center mb-2 flex items-center justify-center gap-1">
                            {{ getMarketDisplayName(market) }}
                            <div v-if="player.markets[market]?.timeline?.some(entry => entry.isTargetTime)" 
                                 class="w-2 h-2 rounded-full bg-orange-500" 
                                 title="Value active at target time">
                            </div>
                            <div v-if="getIsSuspended(player.markets[market])" 
                                 class="w-2 h-2 rounded-full bg-yellow-500" 
                                 title="Market is suspended">
                            </div>
                            <div v-if="getIsClosed(player.markets[market])" 
                                 class="w-2 h-2 rounded-full bg-red-500" 
                                 title="Market is closed">
                            </div>
                          </div>
                          
                          <!-- Market Status Indicator -->
                          <div v-if="getIsSuspended(player.markets[market]) || getIsClosed(player.markets[market])" 
                               class="text-center mb-2">
                            <span v-if="getIsSuspended(player.markets[market])" 
                                  class="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 font-medium">
                              Suspended
                            </span>
                            <span v-else-if="getIsClosed(player.markets[market])" 
                                  class="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800 font-medium">
                              Closed
                            </span>
                          </div>
                          
                          <!-- Market Timeline Data -->
                          <div v-if="player.markets[market] && player.markets[market].timeline" class="flex-1">
                            <!-- Timestamp -->
                            <div class="flex items-center justify-end mb-2">
                              <div class="text-[10px] text-gray-400 text-right leading-tight">
                                {{ formatDateTimeFull(getLatestEntry(player.markets[market])?.created_at) }}
                              </div>
                            </div>

                            <!-- All Balanced Lines Display -->
                            <div v-if="player.markets[market].balancedLines && player.markets[market].balancedLines.length > 0" class="space-y-2">
                              <div v-for="(balancedLine, index) in player.markets[market].balancedLines" :key="index" 
                                   :class="[
                                     'border border-slate-200 rounded-lg p-2 transition-all duration-200 shadow-sm relative',
                                     balancedLine.isTargetTime 
                                       ? 'bg-gradient-to-br from-orange-100 to-orange-200 border-orange-300 shadow-md' 
                                       : 'bg-white hover:shadow-md'
                                   ]">
                                
                                <!-- Suspended Badge -->
                                <div v-if="isSuspendedValue(balancedLine)" 
                                     class="absolute top-[-8px] left-1/2 transform -translate-x-1/2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md z-10">
                                  Suspended
                                </div>
                                  
                                <!-- DateTime Header -->
                                <div class="text-[10px] text-slate-600 text-center mb-2 font-medium border-b border-slate-200 pb-1">
                                  {{ formatDateTimeFull(balancedLine.created_at) }}
                                </div>
                                
                                <!-- Balance Line Value -->
                                <div class="text-center mb-2">
                                  <div class="text-sm font-bold text-slate-800">
                                    {{ balancedLine.balance_line?.toFixed(1) || '0.0' }} | {{ balancedLine?.settlement_value }}
                                  </div>
                                </div>
                                
                                <div class="flex items-center">
                                  <!-- Over/Under Odds -->
                                  <div class="flex-1 space-y-2">
                                    <!-- Over Section -->
                                    <div :class="[
                                      'rounded-lg px-2 py-1.5 flex items-center justify-between text-xs border',
                                      isSuspendedValue(balancedLine) || isClosedValue(balancedLine)
                                        ? 'bg-gray-100 border-gray-300 opacity-50 grayscale'
                                        : 'bg-green-50 border-green-200'
                                    ]">
                                      <div class="flex items-center gap-2 relative">
                                        <div :class="[
                                          'w-2 h-2 rounded-full',
                                          isSuspendedValue(balancedLine) || isClosedValue(balancedLine)
                                            ? 'bg-gray-400'
                                            : 'bg-green-500'
                                        ]"></div>
                                        <span :class="[
                                          'font-semibold',
                                          isSuspendedValue(balancedLine) || isClosedValue(balancedLine)
                                            ? 'text-gray-600'
                                            : 'text-green-700'
                                        ]">O|</span>
                                      </div>
                                      <div class="flex items-center gap-3">
                                        <span class="font-bold text-slate-800">{{ balancedLine.balance_line_over_odds?.toFixed(2) || '0.00' }}</span>
                                        <span class="text-slate-500 font-medium">{{ balancedLine.balance_line_over_probability ? (balancedLine.balance_line_over_probability * 100).toFixed(1) + '%' : '0.0%' }}</span>
                                      </div>
                                    </div>
                                    
                                    <!-- Under Section -->
                                    <div :class="[
                                      'rounded-lg px-2 py-1.5 flex items-center justify-between text-xs border',
                                      isSuspendedValue(balancedLine) || isClosedValue(balancedLine)
                                        ? 'bg-gray-100 border-gray-300 opacity-50 grayscale'
                                        : 'bg-red-50 border-red-200'
                                    ]">
                                      <div class="flex items-center gap-2">
                                        <div :class="[
                                          'w-2 h-2 rounded-full',
                                          isSuspendedValue(balancedLine) || isClosedValue(balancedLine)
                                            ? 'bg-gray-400'
                                            : 'bg-red-500'
                                        ]"></div>
                                        <span :class="[
                                          'font-semibold',
                                          isSuspendedValue(balancedLine) || isClosedValue(balancedLine)
                                            ? 'text-gray-600'
                                            : 'text-red-700'
                                        ]">U|</span>
                                      </div>
                                      <div class="flex items-center gap-3">
                                        <span class="font-bold text-slate-800">{{ balancedLine.balance_line_under_odds?.toFixed(2) || '0.00' }}</span>
                                        <span class="text-slate-500 font-medium">{{ balancedLine.balance_line_under_probability ? (balancedLine.balance_line_under_probability * 100).toFixed(1) + '%' : '0.0%' }}</span>
                                      </div>
                                    </div>

                                    <div class="text-center text-xs text-slate-500">{{ balancedLine.user_id }}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <!-- Fallback: Show latest timeline entry if no balanced lines -->
                            <div v-else class="flex items-center h-full">
                              <!-- Left Side - Balance Line -->
                              <div class="flex-shrink-0 w-12 flex items-center justify-center">
                                <div :class="[
                                  'text-2xl font-bold transition-all duration-200',
                                  getLatestEntry(player.markets[market])?.isTargetTime 
                                    ? 'text-orange-800 bg-gradient-to-br from-orange-100 to-orange-200 px-3 py-2 rounded-lg shadow-sm border border-orange-200' 
                                    : 'text-slate-800'
                                ]">
                                  {{ getLatestEntry(player.markets[market])?.balance_line?.toFixed(1) || '0.0' }}
                                </div>
                              </div>
                              
                              <!-- Right Side - Over/Under Odds -->
                              <div class="flex-1 ml-4 space-y-3">
                                <!-- Over Section -->
                                <div :class="[
                                  'rounded-lg px-4 py-3 flex items-center justify-between text-xs transition-all duration-200 shadow-sm border',
                                  isSuspendedValue(getLatestEntry(player.markets[market])) || isClosedValue(getLatestEntry(player.markets[market]))
                                    ? 'bg-gray-100 border-gray-300 opacity-50 grayscale'
                                    : getLatestEntry(player.markets[market])?.isTargetTime 
                                      ? 'bg-gradient-to-br from-green-100 to-green-200 border-green-300 shadow-md' 
                                      : 'bg-green-50 border-green-200 hover:shadow-md'
                                ]">
                                  <div class="flex items-center gap-2">
                                    <div :class="[
                                      'w-2 h-2 rounded-full',
                                      isSuspendedValue(getLatestEntry(player.markets[market])) || isClosedValue(getLatestEntry(player.markets[market]))
                                        ? 'bg-gray-400'
                                        : 'bg-green-500'
                                    ]"></div>
                                    <span :class="[
                                      'font-semibold',
                                      isSuspendedValue(getLatestEntry(player.markets[market])) || isClosedValue(getLatestEntry(player.markets[market]))
                                        ? 'text-gray-600'
                                        : 'text-green-700'
                                    ]">O|</span>
                                  </div>
                                  <div class="flex items-center gap-3">
                                    <span class="font-bold text-slate-800">{{ getLatestEntry(player.markets[market])?.balance_line_over_odds?.toFixed(2) || '0.00' }}</span>
                                    <span class="text-slate-500 font-medium">{{ getLatestEntry(player.markets[market])?.balance_line_over_probability ? (getLatestEntry(player.markets[market]).balance_line_over_probability * 100).toFixed(1) + '%' : '0.0%' }}</span>
                                  </div>
                                </div>
                                
                                <!-- Under Section -->
                                <div :class="[
                                  'rounded-lg px-4 py-3 flex items-center justify-between text-xs transition-all duration-200 shadow-sm border',
                                  isSuspendedValue(getLatestEntry(player.markets[market])) || isClosedValue(getLatestEntry(player.markets[market]))
                                    ? 'bg-gray-100 border-gray-300 opacity-50 grayscale'
                                    : getLatestEntry(player.markets[market])?.isTargetTime 
                                      ? 'bg-gradient-to-br from-red-100 to-red-200 border-red-300 shadow-md' 
                                      : 'bg-red-50 border-red-200 hover:shadow-md'
                                ]">
                                  <div class="flex items-center gap-2">
                                    <div :class="[
                                      'w-2 h-2 rounded-full',
                                      isSuspendedValue(getLatestEntry(player.markets[market])) || isClosedValue(getLatestEntry(player.markets[market]))
                                        ? 'bg-gray-400'
                                        : 'bg-red-500'
                                    ]"></div>
                                    <span :class="[
                                      'font-semibold',
                                      isSuspendedValue(getLatestEntry(player.markets[market])) || isClosedValue(getLatestEntry(player.markets[market]))
                                        ? 'text-gray-600'
                                        : 'text-red-700'
                                    ]">U|</span>
                                  </div>
                                  <div class="flex items-center gap-3">
                                    <span class="font-bold text-slate-800">{{ getLatestEntry(player.markets[market])?.balance_line_under_odds?.toFixed(2) || '0.00' }}</span>
                                    <span class="text-slate-500 font-medium">{{ getLatestEntry(player.markets[market])?.balance_line_under_probability ? (getLatestEntry(player.markets[market]).balance_line_under_probability * 100).toFixed(1) + '%' : '0.0%' }}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <!-- No Data State -->
                          <div v-else class="text-center text-slate-400 flex-1 flex flex-col justify-center">
                            <div class="text-lg font-bold text-slate-500">0.0</div>
                            <div class="text-xs font-medium text-slate-400">No Data</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  fixtureId: {
    type: [String, Number],
    required: true
  }
})

const emit = defineEmits(['close'])

// Reactive data
const loading = ref(false)
const error = ref(null)
const timelineData = ref([])
const summary = ref({})
const playersList = ref({})
const fixtureData = ref({})
const showTargetTimeData = ref(false)

// Filters
const filters = ref({
  marketType: '',
  playerId: '',
  startDate: '',
  endDate: '',
  targetTime: '',
  isBalanced: '1'  // Always fetch only balanced lines (is_balanced = 1)
})

// Group data by player and market with timeline
const groupedData = computed(() => {
  const grouped = new Map()
  
  // First, group by player
  timelineData.value.forEach(entry => {
    const playerId = entry.player_id
    
    if (!grouped.has(playerId)) {
      // Use players_list for player name if available, otherwise fallback to entry data
      const playerName = playersList.value[playerId] || entry.player_name
      
      grouped.set(playerId, {
        player_id: playerId,
        player_name: playerName,
        player_team_name: entry.player_team_name,
        markets: {},
        latest_entry: entry
      })
    }
    
    // Store the latest entry for this player
    if (new Date(entry.created_at) > new Date(grouped.get(playerId).latest_entry.created_at)) {
      grouped.get(playerId).latest_entry = entry
    }
  })
  
  // Then, group by market for each player and create timeline
  timelineData.value.forEach(entry => {
    const playerId = entry.player_id
    const marketType = entry.market_type
    
    if (!grouped.get(playerId).markets[marketType]) {
      grouped.get(playerId).markets[marketType] = {
        timeline: [],
        balancedLines: []
      }
    }
    
    // Add to timeline (all entries)
    grouped.get(playerId).markets[marketType].timeline.push(entry)
    
    // Separate balanced lines for special display
    if (entry.is_balanced === 1) {
      grouped.get(playerId).markets[marketType].balancedLines.push(entry)
    }
  })
  
  // Sort timeline by created_at (newest first)
  grouped.forEach(player => {
    Object.keys(player.markets).forEach(marketType => {
      if (player.markets[marketType].timeline) {
        player.markets[marketType].timeline.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        )
      }
      if (player.markets[marketType].balancedLines) {
        player.markets[marketType].balancedLines.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        )
      }
    })
  })
  
  return Array.from(grouped.values())
})

// Add target time highlighting to existing data
const enhancedGroupedData = computed(() => {
  const dataToEnhance = groupedData.value
  
  if (!showTargetTimeData.value || !filters.value.targetTime) {
    return dataToEnhance
  }
  
  const targetDate = new Date(filters.value.targetTime)
  
  return dataToEnhance.map(player => {
    const enhancedPlayer = {
      ...player,
      markets: {}
    }
    
    Object.keys(player.markets).forEach(marketType => {
      const marketData = { ...player.markets[marketType] }
      const timeline = marketData.timeline
      
      if (timeline && timeline.length > 0) {
        // Find the closest past entry to target time
        const validEntries = timeline.filter(entry => {
          const entryDate = new Date(entry.created_at)
          return !isNaN(entryDate.getTime()) && entryDate <= targetDate
        })
        
        if (validEntries.length > 0) {
          // Find closest past entry
          const closestEntry = validEntries.reduce((closest, current) => {
            const closestDate = new Date(closest.created_at)
            const currentDate = new Date(current.created_at)
            const targetDiff = Math.abs(targetDate - closestDate)
            const currentDiff = Math.abs(targetDate - currentDate)
            
            return currentDiff < targetDiff ? current : closest
          })
          
          // Mark the closest entry as target time
          marketData.timeline = timeline.map(entry => ({
            ...entry,
            isTargetTime: entry.created_at === closestEntry.created_at
          }))
          
          marketData.balancedLines = marketData.balancedLines?.map(entry => ({
            ...entry,
            isTargetTime: entry.created_at === closestEntry.created_at
          })) || []
        }
      }
      
      enhancedPlayer.markets[marketType] = marketData
    })
    
    return enhancedPlayer
  })
})

// Helper functions for checking suspended/closed status
const isSuspendedValue = (entry) => {
  if (!entry) return false
  // Check if is_suspended is truthy (1, true, '1', etc.)
  return entry.is_suspended === 1 || entry.is_suspended === true || entry.is_suspended === '1'
}

const isClosedValue = (entry) => {
  if (!entry) return false
  // Check if is_closed is truthy (1, true, '1', etc.)
  return entry.is_closed === 1 || entry.is_closed === true || entry.is_closed === '1'
}

const getIsSuspended = (market) => {
  if (!market || !market.timeline || market.timeline.length === 0) return false
  // Check latest entry
  return isSuspendedValue(market.timeline[0])
}

const getIsClosed = (market) => {
  if (!market || !market.timeline || market.timeline.length === 0) return false
  // Check latest entry
  return isClosedValue(market.timeline[0])
}

const getLatestEntry = (market) => {
  if (!market || !market.timeline || market.timeline.length === 0) return null
  return market.timeline[0]
}

const loadTimelineData = async () => {
  if (!props.fixtureId) return
  
  loading.value = true
  error.value = null
  
  try {
    const params = new URLSearchParams()
    
    if (filters.value.marketType) params.append('market_type', filters.value.marketType)
    if (filters.value.playerId) params.append('player_id', filters.value.playerId)
    if (filters.value.startDate) params.append('start_date', filters.value.startDate)
    if (filters.value.endDate) params.append('end_date', filters.value.endDate)
    if (filters.value.isBalanced !== '') params.append('is_balanced', filters.value.isBalanced)
    
    const response = await axios.get(`/api/timeline-clickhouse/${props.fixtureId}?${params.toString()}`)

    if (response.data.success) {
      timelineData.value = response.data.data || []
      summary.value = response.data.meta || {}
      playersList.value = response.data.meta?.players_list || {}
      fixtureData.value = response.data.meta?.fixture_data || {}
    } else {
      throw new Error(response.data.message || 'Failed to load timeline data')
    }
  } catch (err) {
    console.error('Error loading timeline data:', err)
    error.value = err.response?.data?.message || err.message || 'Failed to load timeline data'
  } finally {
    loading.value = false
  }
}

const getMarketDisplayName = (market) => {
  const marketNames = {
    'points': 'Points',
    'total_rebounds': 'Rebounds',
    'assists': 'Assists',
    'three_point_field_goal': '3 Points',
    'turnovers': 'Turnovers',
    'blocks': 'Blocks',
    'steals': 'Steals',
    'pra': 'PRA',
    'pr': 'PR',
    'pa': 'PA',
    'ra': 'RA',
    'bs': 'BS'
  }
  return marketNames[market] || market.replace('_', ' ')
}

const formatTime = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  if (isNaN(date.getTime())) return timestamp
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

const formatDateTimeFull = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  if (isNaN(date.getTime())) return timestamp
  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

// Watchers
watch(() => props.show, (newValue) => {
  if (newValue && props.fixtureId) {
    loadTimelineData()
  }
})

watch(() => props.fixtureId, (newValue) => {
  if (props.show && newValue) {
    loadTimelineData()
  }
})

// Lifecycle
onMounted(() => {
  if (props.show && props.fixtureId) {
    loadTimelineData()
  }
})
</script>

<style scoped>
/* Custom scrollbar */
.overflow-auto::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.overflow-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Horizontal scrollbar for table */
.overflow-x-auto::-webkit-scrollbar {
  height: 8px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Ensure table cells have consistent width */
.grid-cols-12 > div {
  min-width: 180px;
  width: 180px;
}

/* Market cell styling for better spacing */
.market-cell {
  min-width: 180px;
  width: 180px;
  padding: 12px;
}

/* Improve text spacing in market cells */
.market-cell .text-xs {
  line-height: 1.2;
  margin-bottom: 2px;
}

/* Better spacing for over/under sections */
.market-cell .flex {
  gap: 4px;
}
</style>
